/**
 * Wires the account-setup controller to the live session (tokens, probe, metrics); one controller
 * per authenticated session, disposed on unmount. One policy lives here: when the gate actually
 * waited before releasing, the current-user cache was fetched while the user had no roles, so the
 * release is held until a real refetch resolves — or until a deadline, since the refetch has no
 * client timeout — otherwise the permissions-required page would stick. An instant release skips
 * that round trip.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { CountMetric } from '@amzn/ihm-associate-tools-js-lib'
import { getStoreId } from '@utils/EnvironmentUtil'
import { authService } from '@services/auth/AuthService'
import { oauthTokenStore } from '@services/auth/web/OAuthTokenStore'
import { refetchCurrentUser, RefetchResult } from '@api/userAndStoreContext'
import { AccountSetupController, AccountSetupPhase } from '@services/accountSetup/accountSetupController'
import { fetchRekeyMarker, resolveUserInfoEndpoint } from '@services/accountSetup/userInfoProbe'

function emitGateMetric (name: string, value: number): void {
    new CountMetric(getStoreId(), 'Count', 'JWOStoreManagementPlatform', name).count(value)
}

/**
 * One guaranteed-fresh roles fetch, with one retry on a failed fetch; emits the release-refetch
 * counters. Never rejects.
 */
async function releaseRefetch (): Promise<RefetchResult> {
    let result = await refetchCurrentUser()
    if (result === 'failed') {
        emitGateMetric('AccountSetup.ReleaseRefetchFailed', 1)
        result = await refetchCurrentUser()
    }
    if (result === 'timeout') {
        emitGateMetric('AccountSetup.ReleaseRefetchTimeout', 1)
    }
    return result
}

/**
 * Cap on holding a waited release for the roles refetch: a hung GetCurrentUser (bare fetch, no
 * client timeout) must not pin the loader forever.
 */
export const RELEASE_REFETCH_DEADLINE_MS = 8000

export interface IAccountSetupGateState {
    phase: AccountSetupPhase
    keepWaiting: () => void
}

export function useAccountSetupGate (enabled: boolean): IAccountSetupGateState {
    const controllerRef = useRef<AccountSetupController | null>(null)
    const [phase, setPhase] = useState<AccountSetupPhase>('checking')

    useEffect(() => {
        if (!enabled) {
            setPhase('checking')
            return
        }
        const controller = new AccountSetupController({
            getIdToken: () => oauthTokenStore.getIdToken(),
            canProbeWithoutClaims: () => Boolean((window as any).ReactNativeWebView),
            getAccessToken: () => authService.getAccessToken(),
            resolveUserInfoEndpoint: (accessToken) => resolveUserInfoEndpoint({
                tokenEndpoint: authService.getProviderConfigSync()?.tokenEndpoint ?? null,
                accessToken,
            }),
            probe: fetchRekeyMarker,
            forceTokenRefresh: () => authService.forceRefreshAccessToken(),
            emitMetric: emitGateMetric,
            setTimeoutFn: (callback, delayMs) => window.setTimeout(callback, delayMs),
            clearTimeoutFn: (handle) => window.clearTimeout(handle as number),
            now: () => Date.now(),
            random: () => Math.random(),
        })
        controllerRef.current = controller
        let waited = false
        let cancelled = false
        const unsubscribe = controller.subscribe((next) => {
            if (next === 'waiting' || next === 'still_waiting') {
                waited = true
            }
            if (next === 'ready' && waited) {
                // A waited release means the roles cache was fetched pre-completion, so release
                // only after a real refetch (retried once on a failed fetch) — raced against a
                // deadline so a slow fetch cannot pin the loader with no buttons. A late result
                // still lands via onCurrentUserChanged → App re-render.
                let deadlineHandle: number | undefined
                void Promise.race([
                    releaseRefetch().catch(() => undefined),
                    new Promise<void>((resolve) => {
                        deadlineHandle = window.setTimeout(resolve, RELEASE_REFETCH_DEADLINE_MS)
                    }),
                ]).then(() => {
                    window.clearTimeout(deadlineHandle)
                    if (!cancelled) {
                        setPhase('ready')
                    }
                })
                return
            }
            setPhase(next)
        })
        void controller.start()
        return () => {
            cancelled = true
            unsubscribe()
            controller.dispose()
            controllerRef.current = null
        }
    }, [enabled])

    const keepWaiting = useCallback(() => {
        controllerRef.current?.keepWaiting()
    }, [])

    return { phase, keepWaiting }
}
